import { useEffect, useState } from 'react';
import {
  Paper, TextField, Button, Typography, Grid, MenuItem, Stack, Divider, IconButton, Alert,
  Checkbox, FormControlLabel,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { officerNav } from '../components/officerNav';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import TenderItemsEditor, { itemsToPayload } from '../components/TenderItemsEditor';

const DOC_TYPES = ['RFP', 'TOR', 'BOQ', 'OTHER'];

export default function CreateTender() {
  const navigate = useNavigate();
  const { user, canEnterHistorical } = useAuth();
  const [mdas, setMdas] = useState([]);
  const [form, setForm] = useState({
    reference: '',
    title: '', description: '', category: '', mdaId: '',
    eligibility: '', contactDetails: '',
    publicationDate: '', submissionDeadline: '', bidOpeningDate: '',
    procurementMethod: '', procurementMethodDetails: '',
    estimatedValue: '', currency: 'NGN', estimatedValueSource: '',
    isHistorical: false,
    lot: '',
    financialYear: '',
  });
  const [criteria, setCriteria] = useState([{ description: '', weight: '' }]);
  const [docs, setDocs] = useState([]);
  const [items, setItems] = useState([]);
  // Budget year follows the publication year until the officer types one.
  const [yearTouched, setYearTouched] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get('/meta/mdas');
      setMdas(data.data);
      if (user?.mdaId) setForm((f) => ({ ...f, mdaId: user.mdaId }));
    })();
  }, [user]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setPublicationDate = (e) => {
    const value = e.target.value;
    setForm((f) => ({
      ...f,
      publicationDate: value,
      financialYear: yearTouched || !value ? f.financialYear : value.slice(0, 4),
    }));
  };
  const setFinancialYear = (e) => {
    setYearTouched(true);
    setForm((f) => ({ ...f, financialYear: e.target.value }));
  };
  const setCriterion = (i, k) => (e) => setCriteria((c) => c.map((x, j) => (j === i ? { ...x, [k]: e.target.value } : x)));
  const addCriterion = () => setCriteria((c) => [...c, { description: '', weight: '' }]);
  const removeCriterion = (i) => setCriteria((c) => c.filter((_, j) => j !== i));

  const save = async (publish) => {
    setError('');
    setBusy(true);
    try {
      const payload = {
        ...form,
        evaluationCriteria: criteria.filter((c) => c.description.trim()),
        items: itemsToPayload(items),
      };
      const { data } = await api.post('/tenders', payload);
      const tenderId = data.data.id;

      // Upload any attached documents.
      for (const d of docs) {
        const fd = new FormData();
        fd.append('file', d.file);
        fd.append('docType', d.docType);
        // eslint-disable-next-line no-await-in-loop
        await api.post(`/tenders/${tenderId}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }

      if (publish) await api.post(`/tenders/${tenderId}/publish`);
      navigate('/officer');
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Could not save the tender. Check the fields and try again.');
    } finally {
      setBusy(false);
    }
  };

  const addDocRow = () => setDocs((d) => [...d, { docType: 'RFP', file: null }]);
  const setDocFile = (i) => (e) => setDocs((d) => d.map((x, j) => (j === i ? { ...x, file: e.target.files[0] } : x)));
  const setDocType = (i) => (e) => setDocs((d) => d.map((x, j) => (j === i ? { ...x, docType: e.target.value } : x)));

  return (
    <DashboardLayout title="Create Tender" nav={officerNav}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Basic information</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField label="Tender Number" value={form.reference} onChange={set('reference')} fullWidth
              helperText="Enter the number your ministry issued, if it has one. Leave blank to generate one, e.g. TSG/MOE/2026/001." />
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField label="Lot" value={form.lot} onChange={set('lot')} fullWidth
              placeholder="e.g. 2A"
              helperText="Optional. Only if this is one lot of several; used when the number is generated." />
          </Grid>
          <Grid item xs={12} sm={6}><TextField label="Tender Title" value={form.title} onChange={set('title')} fullWidth required /></Grid>
          <Grid item xs={12} sm={6}>
            <TextField select label="MDA" value={form.mdaId} onChange={set('mdaId')} fullWidth required>
              {mdas.map((m) => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}><TextField label="Category" value={form.category} onChange={set('category')} fullWidth required /></Grid>
          <Grid item xs={12}><TextField label="Description" value={form.description} onChange={set('description')} fullWidth required multiline minRows={4} /></Grid>
          <Grid item xs={12}><TextField label="Eligibility criteria" value={form.eligibility} onChange={set('eligibility')} fullWidth multiline minRows={2} /></Grid>
          <Grid item xs={12}><TextField label="Contact details" value={form.contactDetails} onChange={set('contactDetails')} fullWidth /></Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Procurement details</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField select label="Procurement method" value={form.procurementMethod}
              onChange={set('procurementMethod')} fullWidth
              helperText="Leave unset if the method is not known.">
              <MenuItem value="">&mdash; Not stated &mdash;</MenuItem>
              <MenuItem value="OPEN">Open competitive bidding</MenuItem>
              <MenuItem value="SELECTIVE">Selective / restricted tendering</MenuItem>
              <MenuItem value="LIMITED">Limited tendering</MenuItem>
              <MenuItem value="DIRECT">Direct procurement</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Method as advertised" value={form.procurementMethodDetails}
              onChange={set('procurementMethodDetails')} fullWidth
              placeholder="e.g. Restricted Tendering"
              helperText="The local term used in the advertisement, if it differs." />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="Estimated value" type="number" value={form.estimatedValue}
              onChange={set('estimatedValue')} fullWidth
              helperText="Pre-award estimate, before any award figure." />
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField label="Currency" value={form.currency} onChange={set('currency')} fullWidth />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField select label="Where the figure came from" value={form.estimatedValueSource}
              onChange={set('estimatedValueSource')} fullWidth>
              <MenuItem value="">&mdash; Not stated &mdash;</MenuItem>
              <MenuItem value="BUDGET">Budget provision</MenuItem>
              <MenuItem value="ADVERTISED">As advertised</MenuItem>
              <MenuItem value="UNKNOWN">Not known</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField label="Budget year" type="number" value={form.financialYear}
              onChange={setFinancialYear} fullWidth inputProps={{ min: 2000, max: 2100 }}
              helperText="The budget year this is funded from. Follows the publication year unless you change it." />
          </Grid>
          {canEnterHistorical && (
            <Grid item xs={12}>
              <FormControlLabel
                control={<Checkbox checked={form.isHistorical}
                  onChange={(e) => setForm((f) => ({ ...f, isHistorical: e.target.checked }))} />}
                label="This is a historical record, transcribed from paper"
              />
            </Grid>
          )}
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Items being procured</Typography>
        <TenderItemsEditor items={items} onChange={setItems} currency={form.currency} />
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Timeline</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}><TextField label="Publication Date" type="date" value={form.publicationDate} onChange={setPublicationDate} fullWidth InputLabelProps={{ shrink: true }} /></Grid>
          <Grid item xs={12} sm={4}><TextField label="Submission Deadline" type="datetime-local" value={form.submissionDeadline} onChange={set('submissionDeadline')} fullWidth InputLabelProps={{ shrink: true }} /></Grid>
          <Grid item xs={12} sm={4}><TextField label="Bid Opening Date" type="datetime-local" value={form.bidOpeningDate} onChange={set('bidOpeningDate')} fullWidth InputLabelProps={{ shrink: true }} /></Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Evaluation criteria</Typography>
        {criteria.map((c, i) => (
          <Stack direction="row" spacing={2} key={i} sx={{ mb: 2 }} alignItems="center">
            <TextField label="Criterion" value={c.description} onChange={setCriterion(i, 'description')} fullWidth />
            <TextField label="Weight" type="number" value={c.weight} onChange={setCriterion(i, 'weight')} sx={{ width: 120 }} />
            <IconButton onClick={() => removeCriterion(i)} disabled={criteria.length === 1}>✕</IconButton>
          </Stack>
        ))}
        <Button onClick={addCriterion}>+ Add criterion</Button>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Tender documents</Typography>
        {docs.map((d, i) => (
          <Stack direction="row" spacing={2} key={i} sx={{ mb: 2 }} alignItems="center">
            <TextField select label="Type" value={d.docType} onChange={setDocType(i)} sx={{ width: 140 }}>
              {DOC_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
            <Button component="label" variant="outlined">
              {d.file ? d.file.name : 'Choose file'}
              <input type="file" hidden accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" onChange={setDocFile(i)} />
            </Button>
          </Stack>
        ))}
        <Button onClick={addDocRow}>+ Add document</Button>
      </Paper>

      <Divider sx={{ my: 2 }} />
      <Stack direction="row" spacing={2}>
        <Button variant="outlined" onClick={() => save(false)} disabled={busy}>Save Draft</Button>
        <Button variant="contained" onClick={() => save(true)} disabled={busy}>Publish Tender</Button>
      </Stack>
    </DashboardLayout>
  );
}
